// frontend/src/components/admin/PlatformAnalyticsTab.tsx
//
// Platform Analytics dashboard (#211 Phase 6).
//
// AGGREGATE-ONLY by design (privacy-by-design): all metrics are bucketed.
// No per-user browseable view. If per-user lookup is needed in the future,
// build it as a deliberate audited tool in a separate ticket.
//
// Five sections, each with stat tiles and a "Download CSV" button:
//   1. Users
//   2. Trips
//   3. Admin Broadcasts (with last-30 table)
//   4. Lifecycle Notifications (cron)
//   5. Friends

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Chip,
} from '@mui/material';
import { Download, Refresh } from '@mui/icons-material';
import { api } from '../../utils/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UsersSection {
  summary: {
    total: number; active: number; new_7d: number; new_30d: number;
    active_7d: number; active_30d: number; verified: number;
  };
  by_role: { role: string; count: number }[];
  by_tier: { tier: string; count: number }[];
}

interface TripsSection {
  summary: {
    total: number; deleted: number; new_7d: number; new_30d: number;
    group_trips: number; individual_trips: number; avg_travelers: number;
  };
  by_status: { status: string; count: number }[];
  by_visibility: { visibility: string; count: number }[];
}

interface BroadcastsSection {
  summary: {
    total_sent: number; active: number; recalled: number;
    sent_7d: number; sent_30d: number;
  };
  by_category: { category: string; sent_count: number }[];
  recent_broadcasts: {
    notification_id: string; message: string; category: string;
    severity: string; recipient_type: string; sent_at: string;
    recalled_at: string | null;
    unique_clicks: number; total_clicks: number; unique_dismissals: number;
  }[];
}

interface LifecycleSection {
  summary: { total_sent: number; sent_7d: number; sent_30d: number };
  by_category: {
    category: string; sent_count: number;
    unique_clicks: number; unique_dismissals: number;
  }[];
}

interface EarlyAccessSection {
  summary: {
    total: number; pending: number; approved: number; rejected: number;
    new_7d: number; new_30d: number;
  };
}
interface FriendsSection {
  summary: {
    friendships: number; pending_invites: number;
    invites_7d: number; invites_30d: number;
    accepted_7d: number; accepted_30d: number;
    acceptance_rate_pct: number;
  };
}

interface OverviewResponse {
  success: boolean;
  generated_at: string;
  users: UsersSection;
  trips: TripsSection;
  broadcasts: BroadcastsSection;
  lifecycle: LifecycleSection;
  friends: FriendsSection;
  early_access: EarlyAccessSection;
}

// ---------------------------------------------------------------------------
// CSV helper — client-side blob download
// ---------------------------------------------------------------------------

function downloadCSV(filename: string, headers: string[], rows: (string | number | null)[][]) {
  const escape = (v: string | number | null): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const lines = [
    headers.map(escape).join(','),
    ...rows.map(r => r.map(escape).join(',')),
  ];
  const csv = lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const today = new Date().toISOString().slice(0, 10);
  a.download = `percoro_${filename}_${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------------

const StatTile: React.FC<{ label: string; value: number | string; sub?: string }> = ({ label, value, sub }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ py: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PlatformAnalyticsTab: React.FC = () => {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/analytics/overview');
      const result = await response.json();
      if (response.ok && result.success) {
        setData(result);
      } else {
        throw new Error(result.error || 'Failed to load analytics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // CSV exports per section
  const exportUsers = () => {
    if (!data) return;
    const rows: (string | number)[][] = [];
    rows.push(['SUMMARY', '']);
    rows.push(['Total users', data.users.summary.total]);
    rows.push(['Active', data.users.summary.active]);
    rows.push(['New (7 days)', data.users.summary.new_7d]);
    rows.push(['New (30 days)', data.users.summary.new_30d]);
    rows.push(['Active (7 days)', data.users.summary.active_7d]);
    rows.push(['Active (30 days)', data.users.summary.active_30d]);
    rows.push(['Email verified', data.users.summary.verified]);
    rows.push(['', '']);
    rows.push(['BY ROLE', 'Count']);
    data.users.by_role.forEach(r => rows.push([r.role, r.count]));
    rows.push(['', '']);
    rows.push(['BY TIER', 'Count']);
    data.users.by_tier.forEach(r => rows.push([r.tier, r.count]));
    downloadCSV('users_analytics', ['Metric', 'Value'], rows);
  };

  const exportTrips = () => {
    if (!data) return;
    const rows: (string | number)[][] = [];
    rows.push(['SUMMARY', '']);
    rows.push(['Active trips', data.trips.summary.total]);
    rows.push(['Soft-deleted trips', data.trips.summary.deleted]);
    rows.push(['New (7 days)', data.trips.summary.new_7d]);
    rows.push(['New (30 days)', data.trips.summary.new_30d]);
    rows.push(['Group trips', data.trips.summary.group_trips]);
    rows.push(['Individual trips', data.trips.summary.individual_trips]);
    rows.push(['Avg travelers per trip', data.trips.summary.avg_travelers]);
    rows.push(['', '']);
    rows.push(['BY STATUS', 'Count']);
    data.trips.by_status.forEach(r => rows.push([r.status, r.count]));
    rows.push(['', '']);
    rows.push(['BY VISIBILITY', 'Count']);
    data.trips.by_visibility.forEach(r => rows.push([r.visibility, r.count]));
    downloadCSV('trips_analytics', ['Metric', 'Value'], rows);
  };

  const exportBroadcasts = () => {
    if (!data) return;
    const rows: (string | number | null)[][] = data.broadcasts.recent_broadcasts.map(b => [
      b.sent_at,
      b.category,
      b.severity,
      b.recipient_type,
      b.message,
      b.unique_clicks,
      b.total_clicks,
      b.unique_dismissals,
      b.recalled_at ? 'recalled' : 'active',
    ]);
    downloadCSV(
      'admin_broadcasts',
      ['Sent At', 'Category', 'Severity', 'Recipients', 'Message', 'Unique Clicks', 'Total Clicks', 'Unique Dismissals', 'Status'],
      rows
    );
  };

  const exportLifecycle = () => {
    if (!data) return;
    const rows: (string | number)[][] = data.lifecycle.by_category.map(c => [
      c.category, c.sent_count, c.unique_clicks, c.unique_dismissals,
      c.sent_count > 0 ? Math.round((c.unique_clicks / c.sent_count) * 1000) / 10 + '%' : '0%',
    ]);
    downloadCSV(
      'lifecycle_notifications',
      ['Category', 'Sent', 'Unique Clicks', 'Unique Dismissals', 'Click Rate'],
      rows
    );
  };

  const exportFriends = () => {
    if (!data) return;
    const s = data.friends.summary;
    const rows: (string | number)[][] = [
      ['Total accepted friendships', s.friendships],
      ['Pending invites', s.pending_invites],
      ['Invites (7 days)', s.invites_7d],
      ['Invites (30 days)', s.invites_30d],
      ['Accepted (7 days)', s.accepted_7d],
      ['Accepted (30 days)', s.accepted_30d],
      ['Acceptance rate', s.acceptance_rate_pct + '%'],
    ];
    downloadCSV('friends_analytics', ['Metric', 'Value'], rows);
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
        <CircularProgress size={24} />
        <Typography>Loading analytics...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined">Retry</Button>
      </Box>
    );
  }

  if (!data) return null;

  const tealBtn = { bgcolor: '#2D6E6A', color: 'white', '&:hover': { bgcolor: '#009688' } };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Platform Analytics</Typography>
          <Typography variant="caption" color="text.secondary">
            Aggregate metrics only. Generated {new Date(data.generated_at).toLocaleString()}.
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined" size="small">Refresh</Button>
      </Box>



      {/* USERS */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Users</Typography>
            <Button size="small" startIcon={<Download />} onClick={exportUsers} sx={tealBtn}>CSV</Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatTile label="Total" value={data.users.summary.total} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Active" value={data.users.summary.active} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="New (7d)" value={data.users.summary.new_7d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="New (30d)" value={data.users.summary.new_30d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Active (7d)" value={data.users.summary.active_7d} sub="logged in" /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Active (30d)" value={data.users.summary.active_30d} sub="logged in" /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Verified email" value={data.users.summary.verified} /></Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>By role</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {data.users.by_role.map(r => (
                  <Chip key={r.role} label={`${r.role}: ${r.count}`} size="small" />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>By tier</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {data.users.by_tier.map(r => (
                  <Chip key={r.tier} label={`${r.tier}: ${r.count}`} size="small" />
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* FRIENDS */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Friends activity</Typography>
            <Button size="small" startIcon={<Download />} onClick={exportFriends} sx={tealBtn}>CSV</Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatTile label="Friendships" value={data.friends.summary.friendships} sub="accepted" /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Pending invites" value={data.friends.summary.pending_invites} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Acceptance rate" value={data.friends.summary.acceptance_rate_pct + '%'} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Invites (7d)" value={data.friends.summary.invites_7d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Invites (30d)" value={data.friends.summary.invites_30d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Accepted (7d)" value={data.friends.summary.accepted_7d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Accepted (30d)" value={data.friends.summary.accepted_30d} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* EARLY ACCESS */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Early access requests</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatTile label="Total requests" value={data.early_access.summary.total} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Pending" value={data.early_access.summary.pending} sub="awaiting review" /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Approved" value={data.early_access.summary.approved} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Rejected" value={data.early_access.summary.rejected} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="New (7d)" value={data.early_access.summary.new_7d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="New (30d)" value={data.early_access.summary.new_30d} /></Grid>
          </Grid>
        </CardContent>
      </Card>
      {/* TRIPS */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Trips</Typography>
            <Button size="small" startIcon={<Download />} onClick={exportTrips} sx={tealBtn}>CSV</Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatTile label="Active trips" value={data.trips.summary.total} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Soft-deleted" value={data.trips.summary.deleted} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="New (7d)" value={data.trips.summary.new_7d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="New (30d)" value={data.trips.summary.new_30d} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Group trips" value={data.trips.summary.group_trips} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Individual trips" value={data.trips.summary.individual_trips} /></Grid>
            <Grid item xs={6} sm={3}><StatTile label="Avg travelers" value={data.trips.summary.avg_travelers} /></Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>By status</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {data.trips.by_status.map(r => (
                  <Chip key={r.status} label={`${r.status}: ${r.count}`} size="small" />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>By visibility</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {data.trips.by_visibility.map(r => (
                  <Chip key={r.visibility} label={`${r.visibility}: ${r.count}`} size="small" />
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ADMIN BROADCASTS */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Admin broadcasts</Typography>
            <Button size="small" startIcon={<Download />} onClick={exportBroadcasts} sx={tealBtn}>CSV</Button>
          </Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2.4}><StatTile label="Total sent" value={data.broadcasts.summary.total_sent} /></Grid>
            <Grid item xs={6} sm={2.4}><StatTile label="Active" value={data.broadcasts.summary.active} /></Grid>
            <Grid item xs={6} sm={2.4}><StatTile label="Recalled" value={data.broadcasts.summary.recalled} /></Grid>
            <Grid item xs={6} sm={2.4}><StatTile label="Sent (7d)" value={data.broadcasts.summary.sent_7d} /></Grid>
            <Grid item xs={6} sm={2.4}><StatTile label="Sent (30d)" value={data.broadcasts.summary.sent_30d} /></Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Last 30 broadcasts</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Unique clicks</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Dismissals</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.broadcasts.recent_broadcasts.map(b => (
                <TableRow key={b.notification_id}>
                  <TableCell>{new Date(b.sent_at).toLocaleDateString()}</TableCell>
                  <TableCell><Chip label={b.category} size="small" /></TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.message}</TableCell>
                  <TableCell align="center">{b.unique_clicks}</TableCell>
                  <TableCell align="center">{b.unique_dismissals}</TableCell>
                  <TableCell align="center">
                    {b.recalled_at
                      ? <Chip label="Recalled" size="small" color="warning" />
                      : <Chip label="Active" size="small" color="success" />}
                  </TableCell>
                </TableRow>
              ))}
              {data.broadcasts.recent_broadcasts.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center"><Typography variant="caption" color="text.secondary">No broadcasts yet</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* LIFECYCLE NOTIFICATIONS */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Lifecycle notifications (cron)</Typography>
            <Button size="small" startIcon={<Download />} onClick={exportLifecycle} sx={tealBtn}>CSV</Button>
          </Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4}><StatTile label="Total sent" value={data.lifecycle.summary.total_sent} /></Grid>
            <Grid item xs={6} sm={4}><StatTile label="Sent (7d)" value={data.lifecycle.summary.sent_7d} /></Grid>
            <Grid item xs={6} sm={4}><StatTile label="Sent (30d)" value={data.lifecycle.summary.sent_30d} /></Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Sent</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Unique clicks</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Unique dismissals</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Click rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lifecycle.by_category.map(c => {
                const rate = c.sent_count > 0 ? Math.round((c.unique_clicks / c.sent_count) * 1000) / 10 : 0;
                return (
                  <TableRow key={c.category}>
                    <TableCell><Chip label={c.category} size="small" /></TableCell>
                    <TableCell align="center">{c.sent_count}</TableCell>
                    <TableCell align="center">{c.unique_clicks}</TableCell>
                    <TableCell align="center">{c.unique_dismissals}</TableCell>
                    <TableCell align="center">{rate}%</TableCell>
                  </TableRow>
                );
              })}
              {data.lifecycle.by_category.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center"><Typography variant="caption" color="text.secondary">No lifecycle notifications yet</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


    </Box>
  );
};

export default PlatformAnalyticsTab;
